import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Col, Row, Spinner } from 'react-bootstrap';
import {
  FaBuilding,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaThLarge,
} from 'react-icons/fa';
import PropertyStatsRollup from './PropertyStatsRollup';
import PropertyUnitDrillDown from './PropertyUnitDrillDown';
import { fetchPropertyDetailStats } from './propertyApi';

const STATUS_VARIANT = {
  Active: 'success',
  Inactive: 'secondary',
  'Under Construction': 'warning',
  Closed: 'dark',
};

function StatCard({ label, value, accent }) {
  const display = value == null || value === '' ? 0 : value;
  return (
    <div className={`re-stat-card${accent ? ` re-stat-card--${accent}` : ''}`}>
      <div className='re-stat-card__value'>{display}</div>
      <div className='re-stat-card__label'>{label}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') {
    return null;
  }
  return (
    <div className='re-detail-row'>
      <span className='re-detail-row__label'>{label}</span>
      <span className='re-detail-row__value'>{value}</span>
    </div>
  );
}

function PropertySummaryPanel({ property, loading = false, onNavigateToFloors }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!property?.id) {
      setStats(null);
      setDrillDown(null);
      return undefined;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setStatsLoading(true);
    fetchPropertyDetailStats(property.id, controller.signal)
      .then(data => {
        if (!controller.signal.aborted) {
          setStats(data);
        }
      })
      .catch(err => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Property stats load failed:', err);
        if (!controller.signal.aborted) {
          setStats(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setStatsLoading(false);
        }
      });

    return () => controller.abort();
  }, [property?.id]);

  useEffect(() => {
    setDrillDown(null);
  }, [property?.id]);

  if (loading) {
    return (
      <div className='re-summary-empty'>
        <Spinner animation='border' role='status' />
        <p className='mt-3 mb-0'>Loading property…</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className='re-summary-empty'>
        <FaBuilding className='re-summary-empty__icon' />
        <h3>Select a property</h3>
        <p>Search or pick a property from the list to view its summary.</p>
      </div>
    );
  }

  if (drillDown) {
    return (
      <PropertyUnitDrillDown
        drillDown={drillDown}
        propertyId={property.id}
        propertyName={property.name}
        onBack={() => setDrillDown(null)}
        onOpenUnit={({ unitId }) => {
          onNavigateToFloors?.(unitId);
        }}
      />
    );
  }

  const statusVariant = STATUS_VARIANT[property.status] || 'primary';
  const locationLine = [property.city, property.state, property.country]
    .filter(Boolean)
    .join(', ');

  const availableUnits = stats?.inventory?.find(row => row.key === 'Available')?.count ?? 0;
  const occupiedUnits = stats?.inventory?.find(row => row.key === 'Occupied')?.count ?? 0;
  const highDemandUnits = stats?.demand?.find(row => row.tier === 'high')?.count ?? 0;

  const openRecord = () => {
    if (property.id) {
      navigate(`/view/property/${property.id}`);
    }
  };

  const handleDemandClick = drill => {
    if (drill?.kind === 'demand' && drill.value === 'high' && highDemandUnits > 0) {
      onNavigateToFloors?.();
      return;
    }
    setDrillDown(drill);
  };

  return (
    <div className='re-summary'>
      <div className='re-summary__header'>
        <div className='re-summary__title-block'>
          <div className='re-summary__icon'>
            <FaBuilding />
          </div>
          <div>
            <h2 className='re-summary__title'>{property.name}</h2>
            <div className='re-summary__meta'>
              {property.propertyCode && (
                <span className='re-summary__code'>{property.propertyCode}</span>
              )}
              {property.status && (
                <Badge bg={statusVariant} className='re-summary__badge'>
                  {property.status}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className='re-summary__actions'>
          <Button
            variant='outline-primary'
            size='sm'
            onClick={openRecord}
            disabled={!property.id}
          >
            <FaExternalLinkAlt className='me-1' />
            Open Record
          </Button>
        </div>
      </div>

      <div className='re-summary__stats'>
        <StatCard label='Total Floors' value={property.totalFloors} />
        <StatCard label='Total Units' value={stats?.totalUnits ?? property.totalUnits} />
        <StatCard label='Available' value={availableUnits} accent='green' />
        <StatCard label='Occupied' value={occupiedUnits} accent='red' />
      </div>

      <div className='re-summary-card re-summary-card--rollup'>
        <div className='re-summary-card__title-row'>
          <h4 className='re-summary-card__title mb-0'>
            <FaLayerGroup /> Occupancy &amp; demand
          </h4>
          {statsLoading && <Spinner animation='border' size='sm' role='status' />}
          {!statsLoading && stats && (
            <Button
              variant='link'
              size='sm'
              className='re-summary__floors-link p-0'
              onClick={() => onNavigateToFloors?.()}
            >
              View floors
            </Button>
          )}
        </div>
        {stats ? (
          <PropertyStatsRollup
            stats={stats}
            compact
            onInventoryClick={setDrillDown}
            onDemandClick={handleDemandClick}
          />
        ) : !statsLoading ? (
          <p className='re-summary__rollup-empty mb-0'>No unit data available for this property.</p>
        ) : null}
      </div>

      <Row className='g-3 re-summary__grid'>
        <Col md={6}>
          <div className='re-summary-card'>
            <h4 className='re-summary-card__title'>
              <FaMapMarkerAlt /> Location
            </h4>
            <DetailRow label='Address' value={property.address} />
            <DetailRow label='City' value={locationLine} />
            <DetailRow label='Postal Code' value={property.postalCode} />
          </div>
        </Col>
        <Col md={6}>
          <div className='re-summary-card'>
            <h4 className='re-summary-card__title'>
              <FaThLarge /> Classification
            </h4>
            <DetailRow label='Property Type' value={property.propertyType} />
            <DetailRow label='Ownership' value={property.ownershipType} />
            <DetailRow label='Floors' value={String(property.totalFloors ?? 0)} />
            <DetailRow label='Units' value={String(stats?.totalUnits ?? property.totalUnits ?? 0)} />
          </div>
        </Col>
      </Row>

      {property.description && (
        <div className='re-summary-card'>
          <h4 className='re-summary-card__title'>Overview</h4>
          <p className='re-summary__description'>{property.description}</p>
          {stats && stats.totalUnits > 0 && (
            <div className='re-occupancy-bar'>
              <div className='re-occupancy-bar__track'>
                <div
                  className='re-occupancy-bar__segment re-occupancy-bar__segment--occupied'
                  style={{
                    width: `${Math.round((occupiedUnits / stats.totalUnits) * 100)}%`,
                  }}
                  title={`${occupiedUnits} occupied`}
                />
                <div
                  className='re-occupancy-bar__segment re-occupancy-bar__segment--demand'
                  style={{
                    width: `${Math.round((highDemandUnits / stats.totalUnits) * 100)}%`,
                  }}
                  title={`${highDemandUnits} high demand`}
                />
              </div>
              <div className='re-occupancy-bar__legend'>
                <span>
                  <i className='re-dot re-dot--occupied' /> Occupied
                </span>
                <span>
                  <i className='re-dot re-dot--demand' /> High demand
                </span>
                <span>
                  <i className='re-dot re-dot--available' /> Available
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PropertySummaryPanel;
